/**
 * Kazakan Tools 2.0 — World-Class Universal Custom Select Component
 * High-performance, fully accessible, zero-dependency, Apple/Linear aesthetic.
 * Transparently replaces native browser select popups with a classy floating menu
 * while keeping native <select> synced for events, accessibility, and form logic.
 */

const isRTL = () => document.documentElement.dir === 'rtl' || document.documentElement.lang === 'ar';

const CHEVRON_SVG = `
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
`;

const CHECK_SVG = `
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
`;

// Track all active dropdowns to close on document click
let globalListenersAttached = false;
const activeInstances = new Set();

function closeAllCustomSelects(except = null) {
  for (const instance of activeInstances) {
    if (instance !== except) {
      instance.close();
    }
  }
}

function ensureGlobalListeners() {
  if (globalListenersAttached) return;
  globalListenersAttached = true;

  document.addEventListener('pointerdown', (e) => {
    // If click is outside any custom select wrapper, close all
    if (!e.target.closest('.kz-select-wrapper')) {
      closeAllCustomSelects();
    }
  });

  window.addEventListener('resize', () => {
    closeAllCustomSelects();
  }, { passive: true });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeAllCustomSelects();
    }
  });
}

/**
 * Enhances a native <select> element into a custom dropdown.
 * @param {HTMLSelectElement} select 
 * @returns {object} Controller instance
 */
export function enhanceSelect(select) {
  if (!select || select.tagName !== 'SELECT') return null;
  if (select._kzCustomSelect) return select._kzCustomSelect;
  if (
    select.classList.contains('visually-hidden') ||
    select.getAttribute('aria-hidden') === 'true' ||
    select.hasAttribute('data-kz-no-custom') ||
    select.closest('.visually-hidden') ||
    select.closest('.kz-phone-field')
  ) {
    return null;
  }

  ensureGlobalListeners();

  // Create Wrapper
  const wrapper = document.createElement('div');
  wrapper.className = 'kz-select-wrapper';
  if (select.id && wrapper.dataset) wrapper.dataset.forId = select.id;
  if (select.dataset?.span && wrapper.dataset) wrapper.dataset.span = select.dataset.span;

  // Insert wrapper before select and move select inside
  if (select.parentNode) {
    select.parentNode.insertBefore(wrapper, select);
  }
  wrapper.appendChild(select);

  // Visually hide native select
  select.classList.add('kz-native-select-hidden');
  select.tabIndex = -1;
  select.setAttribute('aria-hidden', 'true');

  // Trigger Button
  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'kz-select-trigger';
  trigger.setAttribute('role', 'combobox');
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.setAttribute('aria-expanded', 'false');
  if (select.disabled) trigger.disabled = true;

  const valueSpan = document.createElement('span');
  valueSpan.className = 'kz-select-value';

  const arrowSpan = document.createElement('span');
  arrowSpan.className = 'kz-select-arrow';
  arrowSpan.innerHTML = CHEVRON_SVG;

  trigger.append(valueSpan, arrowSpan);

  // Dropdown Menu Panel
  const dropdown = document.createElement('div');
  dropdown.className = 'kz-select-dropdown';
  dropdown.setAttribute('aria-hidden', 'true');

  // Stop wheel propagation so user scrolling inside dropdown never moves outer page containers
  dropdown.addEventListener('wheel', (e) => {
    e.stopPropagation();
  }, { passive: true });

  const listboxId = select.id ? `kz-listbox-${select.id}` : `kz-listbox-${Math.random().toString(36).slice(2, 9)}`;
  const accessibleName = select.getAttribute('aria-label') ||
    select.labels?.[0]?.textContent?.trim() ||
    select.closest('label')?.querySelector('.batch-label, .pe-label, span')?.textContent?.trim() ||
    select.title ||
    'Options';

  const optionsList = document.createElement('ul');
  optionsList.className = 'kz-select-options';
  optionsList.setAttribute('role', 'listbox');
  optionsList.setAttribute('aria-label', accessibleName);
  optionsList.id = listboxId;
  optionsList.tabIndex = -1;

  trigger.setAttribute('aria-controls', listboxId);
  if (!trigger.getAttribute('aria-label')) {
    trigger.setAttribute('aria-label', accessibleName);
  }

  dropdown.appendChild(optionsList);

  wrapper.append(trigger, dropdown);

  let isOpen = false;
  let focusedOptionIndex = -1;
  let optionItems = [];

  function updateTriggerText() {
    const opts = select.options || select.querySelectorAll?.('option') || [];
    let selectedOption = null;
    const currentVal = select.value !== undefined && select.value !== null ? String(select.value) : '';
    if (currentVal !== '') {
      for (let i = 0; i < opts.length; i++) {
        if (opts[i].value === currentVal) {
          selectedOption = opts[i];
          break;
        }
      }
    }
    if (!selectedOption) {
      const idx = typeof select.selectedIndex === 'number' && select.selectedIndex >= 0 ? select.selectedIndex : 0;
      selectedOption = opts[idx] || opts[0];
    }
    let txt = selectedOption ? (selectedOption.text || selectedOption.textContent || '') : '';
    if (select.id === 'pe-detected-font' || select.id === 'pe-font-family') {
      txt = txt.replace(/\s*\(مستخرج من PDF\)/i, '').replace(/\s*\(from PDF\)/i, '').replace(/\s*\[.*?\]/g, '').trim();
    }
    valueSpan.textContent = txt;
    trigger.title = selectedOption ? (selectedOption.text || selectedOption.textContent || '') : '';
    if ((select.id === 'pe-detected-font' || select.id === 'pe-font-family') && selectedOption?.value) {
      valueSpan.style.fontFamily = selectedOption.value;
    }
  }


  function rebuildOptions() {
    optionsList.replaceChildren();
    optionItems = [];

    const buildItem = (opt, idx, isInGroup = false) => {
      const li = document.createElement('li');
      li.className = 'kz-select-option';
      if (isInGroup) {
        li.classList.add('is-in-group');
      }
      li.setAttribute('role', 'option');
      li.dataset.value = opt.value;
      li.dataset.index = String(idx);

      const isSelected = opt.selected;
      if (isSelected) {
        li.classList.add('is-selected');
        li.setAttribute('aria-selected', 'true');
      } else {
        li.setAttribute('aria-selected', 'false');
      }

      if (opt.disabled) {
        li.classList.add('is-disabled');
        li.setAttribute('aria-disabled', 'true');
      }

      const label = document.createElement('span');
      label.className = 'kz-option-label';

      // High-grade typography preview & clean metadata breakdown for font selectors
      if ((select.id === 'pe-detected-font' || select.id === 'pe-font-family') && opt.value && !opt.value.includes('query') && opt.value !== 'custom') {
        const rawText = opt.text;
        const isExtracted = rawText.includes('مستخرج') || rawText.includes('from PDF');
        const isDefault = rawText.includes('افتراضي') || rawText.includes('Default');

        let cleanText = rawText.replace(/^[🎯✨🔤🌐🌍💻✍️]\s*/, '').replace(/\s*\(مستخرج من PDF\)/i, '').replace(/\s*\(from PDF\)/i, '').replace(/\s*\[.*?\]/g, '').trim();

        const match = cleanText.match(/^([^(—]+)(?:\((.*?)\))?(?:\s*—\s*(.*))?$/);
        const fontName = match ? match[1].trim() : cleanText;
        const fontSub = match && match[2] ? match[2].trim() : (match && match[3] ? match[3].trim() : '');

        label.innerHTML = `
          <div class="kz-font-preview-row">
            <span class="kz-font-name" style="font-family: ${opt.value};">${fontName}</span>
            ${fontSub ? `<span class="kz-font-sub">${fontSub}</span>` : ''}
            ${isExtracted ? `<span class="kz-font-badge kz-badge-extracted">${isRTL() ? 'مستخرج' : 'PDF'}</span>` : ''}
            ${isDefault ? `<span class="kz-font-badge kz-badge-default">${isRTL() ? 'افتراضي' : 'Default'}</span>` : ''}
          </div>
        `;
      } else {
        label.textContent = opt.text;
      }

      const check = document.createElement('span');
      check.className = 'kz-option-check';
      check.innerHTML = CHECK_SVG;

      li.append(label, check);

      li.addEventListener('click', (e) => {
        e.stopPropagation();
        if (opt.disabled) return;
        selectValue(opt.value);
        close();
        trigger.focus({ preventScroll: true });
      });

      li.addEventListener('pointerenter', () => {
        setFocusedIndex(idx, false);
      });

      optionItems.push(li);
      optionsList.appendChild(li);
    };

    let globalIdx = 0;
    for (const child of select.children) {
      if (child.tagName === 'OPTGROUP') {
        const groupLi = document.createElement('li');
        groupLi.className = 'kz-select-optgroup';
        groupLi.setAttribute('role', 'presentation');

        const groupLabel = document.createElement('span');
        groupLabel.className = 'kz-select-optgroup-label';
        groupLabel.textContent = child.label;
        groupLi.appendChild(groupLabel);
        optionsList.appendChild(groupLi);

        for (const opt of child.children) {
          if (opt.tagName === 'OPTION') {
            buildItem(opt, globalIdx++, true);
          }
        }
      } else if (child.tagName === 'OPTION') {
        buildItem(child, globalIdx++, false);
      }
    }

    updateTriggerText();
  }

  function selectValue(val) {
    select.value = val;

    // Sync active classes
    for (const li of optionItems) {
      const match = li.dataset.value === String(val);
      li.classList.toggle('is-selected', match);
      li.setAttribute('aria-selected', match ? 'true' : 'false');
    }

    updateTriggerText();

    // Transparently dispatch native events for existing tool logic
    select.dispatchEvent(new Event('input', { bubbles: true }));
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function syncFromNative() {
    trigger.disabled = Boolean(select.disabled);
    trigger.classList.toggle('is-disabled', Boolean(select.disabled));

    const currentVal = String(select.value);
    for (const li of optionItems) {
      const match = li.dataset.value === currentVal;
      li.classList.toggle('is-selected', match);
      li.setAttribute('aria-selected', match ? 'true' : 'false');
    }
    updateTriggerText();
  }

  function setFocusedIndex(idx, shouldScroll = true) {
    if (idx < 0 || idx >= optionItems.length) return;
    focusedOptionIndex = idx;
    for (let i = 0; i < optionItems.length; i++) {
      const isFocused = i === idx;
      optionItems[i].classList.toggle('is-focused', isFocused);
      if (isFocused && isOpen && shouldScroll) {
        optionItems[i].scrollIntoView({ block: 'nearest' });
      }
    }
  }

  function open() {
    if (isOpen || select.disabled) return;
    closeAllCustomSelects(controller);

    isOpen = true;
    activeInstances.add(controller);

    // Calculate smart positioning (open upwards if space below is limited)
    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const needsUpwards = spaceBelow < 230 && spaceAbove > spaceBelow;

    dropdown.classList.toggle('is-open-upwards', needsUpwards);
    dropdown.classList.add('is-open');
    dropdown.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    wrapper.classList.add('is-active');

    const parentContainer = wrapper.closest('.batch-select-wrap, .batch-setting, .batch-format-hero');
    if (parentContainer) {
      parentContainer.classList.add('is-select-active');
    }

    // Focus current selected option
    const selIdx = select.selectedIndex >= 0 ? select.selectedIndex : 0;
    setFocusedIndex(selIdx);
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    activeInstances.delete(controller);

    dropdown.classList.remove('is-open', 'is-open-upwards');
    dropdown.setAttribute('aria-hidden', 'true');
    trigger.setAttribute('aria-expanded', 'false');
    wrapper.classList.remove('is-active');
    focusedOptionIndex = -1;

    const parentContainer = wrapper.closest('.batch-select-wrap, .batch-setting, .batch-format-hero');
    if (parentContainer) {
      parentContainer.classList.remove('is-select-active');
    }

    for (const item of optionItems) {
      item.classList.remove('is-focused');
    }
  }

  function toggle() {
    if (isOpen) close();
    else open();
  }

  // Trigger click
  trigger.addEventListener('click', (e) => {
    e.preventDefault();
    toggle();
  });

  // Keyboard navigation on trigger
  trigger.addEventListener('keydown', (e) => {
    if (select.disabled) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        open();
      } else {
        const next = Math.min(optionItems.length - 1, focusedOptionIndex + 1);
        setFocusedIndex(next);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) {
        open();
      } else {
        const prev = Math.max(0, focusedOptionIndex - 1);
        setFocusedIndex(prev);
      }
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (isOpen && focusedOptionIndex >= 0 && optionItems[focusedOptionIndex]) {
        const item = optionItems[focusedOptionIndex];
        selectValue(item.dataset.value);
        close();
      } else {
        toggle();
      }
    } else if (e.key === 'Escape' && isOpen) {
      e.preventDefault();
      close();
    } else if (e.key === 'Tab' && isOpen) {
      close();
    }
  });

  // Native select listener for programmatic value changes
  select.addEventListener('change', syncFromNative);
  select.addEventListener('input', syncFromNative);

  // Intercept select.value setter so programmatic assignments automatically sync the custom UI
  const originalDescriptor = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value');
  let hasCustomValueProp = false;
  if (originalDescriptor) {
    try {
      Object.defineProperty(select, 'value', {
        get() {
          return originalDescriptor.get.call(this);
        },
        set(val) {
          originalDescriptor.set.call(this, val);
          syncFromNative();
        },
        configurable: true
      });
      hasCustomValueProp = true;
    } catch {
      // Ignore if cannot be redefined
    }
  }

  // Observe option and disabled changes
  const observer = new MutationObserver(() => {
    rebuildOptions();
    syncFromNative();
  });
  observer.observe(select, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });

  rebuildOptions();
  syncFromNative();

  const controller = {
    wrapper,
    trigger,
    dropdown,
    select,
    open,
    close,
    toggle,
    update: () => {
      rebuildOptions();
      syncFromNative();
    },
    destroy: () => {
      observer.disconnect();
      activeInstances.delete(controller);
      const parentContainer = wrapper.closest('.batch-select-wrap, .batch-setting, .batch-format-hero');
      if (parentContainer) {
        parentContainer.classList.remove('is-select-active');
      }
      if (hasCustomValueProp) {
        delete select.value;
      }
      select.classList.remove('kz-native-select-hidden');
      select.tabIndex = 0;
      select.removeAttribute('aria-hidden');
      select.removeEventListener('change', syncFromNative);
      select.removeEventListener('input', syncFromNative);
      delete select._kzCustomSelect;
      if (wrapper.parentNode) {
        wrapper.parentNode.insertBefore(select, wrapper);
      }
      wrapper.remove();
    }
  };

  select._kzCustomSelect = controller;
  return controller;
}

/**
 * Initializes custom selects on all <select> elements in a container.
 * @param {HTMLElement|Document} root 
 */
export function initCustomSelects(root = document) {
  if (!root) return;
  const selects = root.querySelectorAll('select:not([data-kz-custom-select])');
  for (const sel of selects) {
    if (
      sel.classList.contains('visually-hidden') ||
      sel.getAttribute('aria-hidden') === 'true' ||
      sel.hasAttribute('data-kz-no-custom') ||
      sel.closest('.visually-hidden') ||
      sel.closest('.kz-phone-field')
    ) {
      continue;
    }
    sel.dataset.kzCustomSelect = 'true';
    enhanceSelect(sel);
  }
}

// Auto-run when DOM is ready
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initCustomSelects());
  } else {
    initCustomSelects();
  }
}
